import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Menu,
  MenuItem as MenuItemComponent,
  Divider,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { AgronodButton, AgronodTypography } from '@agronod/mui-components';
import {
  Add,
  Search,
  Edit,
  Delete,
  Visibility,
  Assignment,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { useControls, useControlStats } from '@/hooks/useControls';
import { useShares } from '@/hooks/useShares';
import { Control, ControlFilters, ControlStatus } from '@/types';
import { ROUTES } from '@/constants';
import { buildRenewalUrl, isRenewable } from '@/utils/controls/renewal';
import HeroSection from '@/components/controls/HeroSection';
import ControlProgramCard from '@/components/controls/ControlProgramCard';
import DeleteControlDialog from '@/components/controls/DeleteControlDialog';

const ControlsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<ControlFilters>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedControl, setSelectedControl] = useState<Control | null>(null);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'completed'>('upcoming');
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [controlToComplete, setControlToComplete] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [controlToDelete, setControlToDelete] = useState<string | null>(null);

  const {
    controls,
    isLoading,
    deleteControl,
    updateControl,
    isCreating,
    isDeleting,
  } = useControls(page, 20, filters);

  const { stats: _stats } = useControlStats();
  const { shares, isExpired } = useShares();

  // Create a Set of control IDs that have active (non-expired) shares
  const activeShareControlIds = useMemo(() => {
    const ids = new Set<string>();
    shares.forEach(share => {
      if (share.isActive && !isExpired(share.expiresAt)) {
        ids.add(share.control.id);
      }
    });
    return ids;
  }, [shares, isExpired]);

  // Filter controls based on active tab
  const filteredControls = useMemo(() => {
    if (activeTab === 'completed') {
      return controls.filter(control => control.status === 'COMPLETED');
    }
    // Upcoming: all controls except completed (includes DRAFT, IN_PROGRESS, OVERDUE)
    return controls.filter(control => control.status !== 'COMPLETED');
  }, [controls, activeTab]);

  const completedCount = useMemo(() => {
    return controls.filter(control => control.status === 'COMPLETED').length;
  }, [controls]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'upcoming' | 'completed') => {
    setActiveTab(newValue);
    setPage(1); // Reset to first page when switching tabs
  };

  const handleSearchChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      search: value
    }));
    setPage(1); // Reset to first page when searching
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedControl(null);
  };

  const handleDeleteControl = async (controlId?: string) => {
    const idToDelete = controlId || selectedControl?.id;
    if (idToDelete) {
      setControlToDelete(idToDelete);
      setDeleteDialogOpen(true);
      handleMenuClose();
    }
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setControlToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (controlToDelete) {
      deleteControl(controlToDelete);
      handleDeleteDialogClose();
    }
  };

  const handleMarkCompleted = (controlId: string) => {
    setControlToComplete(controlId);
    setCompletionDialogOpen(true);
  };

  const handleCreateNew = (controlTypeId: string) => {
    // Navigate to create control page with controlTypeId pre-selected
    navigate(`${ROUTES.CREATE_CONTROL}?controlTypeId=${controlTypeId}`);
  };

  const handleRenew = (control: Control) => {
    navigate(buildRenewalUrl(control));
  };

  const handleCompletionDialogClose = () => {
    setCompletionDialogOpen(false);
    setControlToComplete(null);
  };

  const handleConfirmCompletion = async () => {
    if (!controlToComplete) return;

    try {
      await updateControl({
        controlId: controlToComplete,
        updates: {
          status: ControlStatus.COMPLETED,
          // Note: scheduledDate is intentionally NOT updated here to preserve the original date
          // TODO: Add completedAt field to Control schema for proper completion tracking
        },
      });
      enqueueSnackbar('Kontrollen har markerats som genomförd', { variant: 'success' });
      handleCompletionDialogClose();
    } catch (error) {
      console.error('Failed to complete control:', error);
    }
  };
  return (
    <Box sx={{ bgcolor: 'background.page', minHeight: '100vh', py: 3 }}>
      <Container maxWidth="lg">
        {/* Page Header */}
        <Box sx={{
          mb: 3
        }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
          <Box>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontSize: { xs: '1.75rem', sm: '2.125rem' },
              }}
            >
              Kontrollförberedelser
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: { xs: '0.875rem', sm: '0.9375rem' }
              }}>
              Hantera och förbered dokument för olika kontroller och certifieringar
            </Typography>
          </Box>
          <AgronodButton
            variant="outlined"
            startIcon={<Add sx={{ fontSize: { xs: 18, sm: 20 } }} />}
            onClick={() => navigate(ROUTES.CREATE_CONTROL)}
            disabled={isCreating}
            sx={{
              height: 'fit-content',
              width: { xs: '100%', sm: 'auto' },
              whiteSpace: { xs: 'normal', sm: 'nowrap' },
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              borderRadius: '50px',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Lägg till ny kontroll
          </AgronodButton>
        </Box>
      </Box>

      {/* Get-started guide — onboarding only. Hidden once the user has shared
          at least one control (a share implies a control was added and shared,
          i.e. they've been through the whole 1→2→3 flow). */}
      {shares.length === 0 && <HeroSection />}

      {/* Tabs Menu - Pill Style */}
      <Box sx={{ mb: 3, overflow: 'auto', pb: 1, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: '30px',
            p: 0.5,
            display: 'inline-flex',
            gap: { xs: 0.5, sm: 1 },
            minWidth: { xs: 'max-content', sm: 'auto' },
          }}
        >
          <Box
            onClick={() => handleTabChange(null as any, 'upcoming')}
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.75, sm: 1 },
              borderRadius: '24px',
              bgcolor: activeTab === 'upcoming' ? 'primary.light' : 'rgba(255, 255, 255, 0.5)',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: activeTab === 'upcoming' ? 'primary.light' : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: '16px',
                color: 'text.primary',
                whiteSpace: 'nowrap',
              }}
            >
              Kommande
            </Typography>
          </Box>
          <Box
            onClick={() => completedCount > 0 && handleTabChange(null as any, 'completed')}
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.75, sm: 1 },
              borderRadius: '24px',
              bgcolor: activeTab === 'completed' ? 'primary.light' : 'rgba(255, 255, 255, 0.5)',
              cursor: completedCount > 0 ? 'pointer' : 'default',
              opacity: completedCount > 0 ? 1 : 0.5,
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: completedCount > 0
                  ? (activeTab === 'completed' ? 'primary.light' : 'rgba(0, 0, 0, 0.04)')
                  : 'rgba(255, 255, 255, 0.5)',
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: '16px',
                color: 'text.primary',
                whiteSpace: 'nowrap',
              }}
            >
              Genomförda
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Sök kontroller..."
          value={filters.search || ''}
          onChange={(e) => handleSearchChange(e.target.value)}
          slotProps={{
            input: {
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary', fontSize: { xs: 18, sm: 20 } }} />,
            },
          }}
          sx={{
            bgcolor: 'white',
            borderRadius: '8px',
            '& .MuiOutlinedInput-root': {
              height: { xs: '40px', sm: '44px' },
              borderRadius: '8px',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              '& fieldset': {
                borderColor: 'grey.300',
                borderRadius: '8px',
              },
              '&:hover fieldset': {
                borderColor: 'secondary.main',
              },
            },
          }}
        />
      </Box>

      {/* Controls List */}
      <Box>
        {isLoading ? (
          <Grid container spacing={3}>
            {[...Array(4)].map((_, index) => (
              <Grid size={{ xs: 12 }} key={index}>
                <Card variant="outlined">
                  <CardContent>
                    <Skeleton variant="text" width="40%" height={32} />
                    <Skeleton variant="text" width="20%" height={20} sx={{ mt: 1 }} />
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        mt: 2
                      }}>
                      <Skeleton variant="text" width="30%" height={20} />
                      <Skeleton variant="text" width="30%" height={20} />
                    </Box>
                    <Skeleton variant="rectangular" width="100%" height={8} sx={{ mt: 2, borderRadius: 4 }} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : filteredControls.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: { xs: 4, sm: 6 },
              bgcolor: "white",
              borderRadius: 2,
              border: 1,
              borderColor: "grey.200",
              px: 2
            }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'grey.200',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <Assignment sx={{ fontSize: 32, color: 'grey.500' }} />
            </Box>
            <AgronodTypography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              {activeTab === 'completed'
                ? 'Inga genomförda kontroller'
                : 'Inga kommande kontroller'}
            </AgronodTypography>
            <AgronodTypography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: '1.3', display: 'block' }}>
              {filters.search ?
                'Försök ändra din sökning' :
                activeTab === 'completed'
                  ? 'Du har inga genomförda kontroller ännu'
                  : 'Skapa din första kontroll för att komma igång'
              }
            </AgronodTypography>
            <AgronodButton
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate(ROUTES.CREATE_CONTROL)}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                maxWidth: { xs: 300, sm: 'none' },
                borderRadius: '50px',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {activeTab === 'completed' ? 'Skapa ny kontroll' : 'Lägg till ny kontroll'}
            </AgronodButton>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredControls.map((control: Control) => (
              <Grid size={{ xs: 12 }} key={control.id}>
                <ControlProgramCard
                  control={control}
                  hasActiveShare={activeShareControlIds.has(control.id)}
                  onViewDetails={() => navigate(`/controls/${control.id}`)}
                  onDelete={handleDeleteControl}
                  onMarkCompleted={handleMarkCompleted}
                  onCreateNew={handleCreateNew}
                  onRenew={handleRenew}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItemComponent
          onClick={() => {
            if (selectedControl) navigate(`/controls/${selectedControl.id}`);
            handleMenuClose();
          }}
        >
          <Visibility sx={{ mr: 1 }} />
          Visa detaljer
        </MenuItemComponent>
        <MenuItemComponent
          onClick={() => {
            if (selectedControl) navigate(`/controls/${selectedControl.id}/edit`);
            handleMenuClose();
          }}
        >
          <Edit sx={{ mr: 1 }} />
          Redigera
        </MenuItemComponent>
        {selectedControl && isRenewable(selectedControl) && (
          <MenuItemComponent
            onClick={() => {
              handleRenew(selectedControl);
              handleMenuClose();
            }}
          >
            <Add sx={{ mr: 1 }} />
            Lägg till ny kontroll
          </MenuItemComponent>
        )}
        <Divider />
        <MenuItemComponent
          onClick={() => handleDeleteControl()}
          sx={{ color: 'error.main' }}
          disabled={isDeleting}
        >
          <Delete sx={{ mr: 1 }} />
          Ta bort
        </MenuItemComponent>
      </Menu>

      {/* Completion Confirmation Dialog */}
      <Dialog
        open={completionDialogOpen}
        onClose={handleCompletionDialogClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
          Markera kontroll som genomförd
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
            Är du säker på att du vill markera denna kontroll som genomförd? Kontrollen kommer att flyttas till genomförda kontroller.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
          <AgronodButton
            variant="text"
            onClick={handleCompletionDialogClose}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Avbryt
          </AgronodButton>
          <AgronodButton
            variant="contained"
            onClick={handleConfirmCompletion}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '50px',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Markera som genomförd
          </AgronodButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteControlDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
        controlId={controlToDelete}
        isDeleting={isDeleting}
      />
      </Container>
    </Box>
  );
};

export default ControlsPage;